import {
  useGetCandidateBatchLazyQuery,
  useGetCandidateProfileListLazyQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { formatDateFromNow } from 'utils/datetime';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { useCallback, useEffect, useState } from 'react';
import { HeaderBreadcrumb } from 'common/ui/Header';
import { pathsV2Creators } from 'core/services/navigation/usePathsV2';
import { Stack } from '@mui/material';
import { Button } from 'system';
import { useCandidateProfileTalentPoolTableActions } from 'modules/candidate-profile/tables/hooks/useCandidateProfileTalentPoolTableActions';
import { Link } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import {
  CandidateListTab,
  useCandidateListStore
} from 'modules/candidate-profile/stores/useCandidateListStore';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { getTeamsLabel } from 'modules/candidate-profile/utils/getTeamsLabel';

type CandidateProfileTableHeaderOptions = {
  hideBreadcrumbs: boolean;
  currentBreadcrumb: string;
  breadcrumbs: HeaderBreadcrumb[];
  subtitle: string | React.ReactNode;
  items?: string[];
  actions: React.ReactNode | null;
  showBackButton: boolean;
};

const fallbackOptions: CandidateProfileTableHeaderOptions = {
  hideBreadcrumbs: false,
  currentBreadcrumb: 'Loading...',
  breadcrumbs: [],
  subtitle: '',
  actions: null,
  showBackButton: false
};

const talentListsBreadcrumb: HeaderBreadcrumb = {
  name: 'Talent Lists',
  path: pathsV2Creators.CANDIDATE_PROFILE_LIST({
    searchParams: { tab: CandidateListTab.TALENT_LISTS }
  })
};

// A list that doesn't exist or that the user can't see.
const talentListNotFoundOptions: CandidateProfileTableHeaderOptions = {
  hideBreadcrumbs: false,
  currentBreadcrumb: 'Talent list not found',
  breadcrumbs: [talentListsBreadcrumb],
  subtitle: '',
  actions: null,
  showBackButton: true
};

export const useGetCandidateProfileTableHeaderOptions = (
  candidateProfileListProps: CandidateProfileListProps
) => {
  const [options, setOptions] =
    useState<CandidateProfileTableHeaderOptions>(fallbackOptions);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const [getCandidateProfileList] = useGetCandidateProfileListLazyQuery();
  const [getCandidateBatch] = useGetCandidateBatchLazyQuery();
  const { closeDrawer } = useDrawerV2();
  const isTeamsEnabled = Boolean(useFlag(Flags.TALENT_LIST_TEAMS));
  const integrationProvider = useCandidateListStore(
    (state) => state.integrationProvider
  );

  const getCandidateListOptions =
    async (): Promise<CandidateProfileTableHeaderOptions> => {
      return {
        hideBreadcrumbs: true,
        currentBreadcrumb: 'Candidates',
        breadcrumbs: [],
        subtitle: `Found in Popp and ${integrationProvider}.`,
        actions: null,
        showBackButton: false
      };
    };

  const handleUpdateItem = useCallback(() => {
    // Trigger a refetch of the header options to reflect updated data
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  const { actionsRenderer } = useCandidateProfileTalentPoolTableActions({
    candidateProfileListProps,
    updateItem: handleUpdateItem,
    removeItem: () => {
      return;
    }
  });

  const getTalentPoolOptions = async (
    talentPoolId: string
  ): Promise<CandidateProfileTableHeaderOptions> => {
    try {
      const { data } = await getCandidateProfileList({
        variables: {
          id: talentPoolId
        },
        fetchPolicy: 'network-only'
      });
      const list = data?.getCandidateProfileListForUser;
      if (!list) {
        return talentListNotFoundOptions;
      }

      const teamsLabel = isTeamsEnabled ? getTeamsLabel(list.teams) : null;

      return {
        hideBreadcrumbs: false,
        currentBreadcrumb: list.title,
        subtitle: list.description ?? '',
        items: [
          `${list.candidateProfilesCount} candidates`,
          `Owned by ${list.userName ?? 'Unknown'}`,
          ...(teamsLabel ? [teamsLabel] : []),
          `Updated ${formatDateFromNow(list.updatedAt)}`
        ],
        breadcrumbs: [talentListsBreadcrumb],
        actions: (
          <Stack direction="row" gap="8px" alignItems="center">
            <Button
              variant="primary"
              to={pathsV2Creators.TALENT_POOL_ADD_CANDIDATES({ talentPoolId })}
              component={Link}
            >
              Add candidates
            </Button>
            {actionsRenderer({
              id: talentPoolId,
              title: list.title,
              description: list.description ?? null,
              candidatesCount: list.candidateProfilesCount,
              owner: list.userName ?? 'Unknown',
              teams: list.teams,
              updatedAt: list.updatedAt
            })}
          </Stack>
        ),
        showBackButton: true
      };
    } catch {
      return fallbackOptions;
    }
  };

  const getAddCandidatesToTalentPoolOptions = async (
    talentPoolId: string
  ): Promise<CandidateProfileTableHeaderOptions> => {
    try {
      const { data } = await getCandidateProfileList({
        variables: {
          id: talentPoolId
        }
      });
      const talentListTitle = data?.getCandidateProfileListForUser?.title;
      if (!talentListTitle) {
        return talentListNotFoundOptions;
      }

      return {
        hideBreadcrumbs: false,
        currentBreadcrumb: 'Add candidates',
        subtitle: (
          <>
            To{' '}
            <Link
              to={pathsV2Creators.TALENT_POOL_DETAILS({ talentPoolId })}
              onClick={closeDrawer}
            >
              {talentListTitle}
            </Link>{' '}
            talent list
          </>
        ),
        breadcrumbs: [
          {
            name: 'Candidates',
            path: pathsV2Creators.CANDIDATE_PROFILE_LIST({})
          },
          {
            name: talentListTitle,
            path: pathsV2Creators.TALENT_POOL_DETAILS({ talentPoolId })
          }
        ],
        actions: null,
        showBackButton: true
      };
    } catch {
      return fallbackOptions;
    }
  };

  const getAnalysisCandidatesListOptions = async (
    candidateBatchId: string
  ): Promise<CandidateProfileTableHeaderOptions> => {
    try {
      const { data: candidateBatchData } = await getCandidateBatch({
        variables: { id: candidateBatchId }
      });
      return {
        hideBreadcrumbs: false,
        currentBreadcrumb: `Results from ${integrationProvider}`,
        subtitle: `For ${getAnalysisTitle(
          candidateBatchData?.getCandidateBatch
        )}`,
        breadcrumbs: [
          {
            name: 'Analyse',
            path: Paths.ANALYSE
          },
          {
            name: getAnalysisTitle(
              candidateBatchData?.getCandidateBatch,
              'Analysis'
            ),
            path: `${Paths.ANALYSE}/${candidateBatchId}`
          }
        ],
        actions: null,
        showBackButton: false
      };
    } catch {
      return fallbackOptions;
    }
  };

  const getAnalysisCandidatesFromTalentPoolOptions = async (
    talentPoolId: string,
    candidateBatchId: string
  ): Promise<CandidateProfileTableHeaderOptions> => {
    try {
      const { data: candidateBatchData } = await getCandidateBatch({
        variables: { id: candidateBatchId }
      });
      const { data: candidateProfileListData } = await getCandidateProfileList({
        variables: {
          id: talentPoolId
        }
      });
      const talentListTitle =
        candidateProfileListData?.getCandidateProfileListForUser?.title;
      if (!talentListTitle) {
        return talentListNotFoundOptions;
      }

      return {
        hideBreadcrumbs: false,
        currentBreadcrumb: talentListTitle,
        subtitle: `Found in Popp and ${integrationProvider}.`,
        breadcrumbs: [
          {
            name: 'Analyse',
            path: Paths.ANALYSE
          },
          {
            name: getAnalysisTitle(
              candidateBatchData?.getCandidateBatch,
              'Analysis'
            ),
            path: `${Paths.ANALYSE}/${candidateBatchId}`
          },
          {
            name: 'Candidates',
            path: pathsV2Creators.CANDIDATE_PROFILE_LIST({})
          }
        ],
        actions: null,
        showBackButton: true
      };
    } catch {
      return fallbackOptions;
    }
  };

  const getOptions = async () => {
    switch (candidateProfileListProps.type) {
      case CandidateProfileListType.CANDIDATES_LIST:
        return getCandidateListOptions();
      case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
        return getTalentPoolOptions(candidateProfileListProps.talentPoolId);
      case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
        return getAddCandidatesToTalentPoolOptions(
          candidateProfileListProps.talentPoolId
        );
      case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
        return getAnalysisCandidatesListOptions(
          candidateProfileListProps.candidateBatchId
        );
      case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
        return getAnalysisCandidatesFromTalentPoolOptions(
          candidateProfileListProps.talentPoolId,
          candidateProfileListProps.candidateBatchId
        );
    }
  };

  useEffect(() => {
    const fetchOptions = async () => {
      const options = await getOptions();
      setOptions(options);
    };
    fetchOptions();
  }, [
    JSON.stringify(candidateProfileListProps),
    refetchTrigger,
    integrationProvider,
    isTeamsEnabled
  ]);

  return { options };
};
